/* The clock on a support session.
 *
 * A support session is the one piece of state in this app that is dangerous
 * because it is FORGOTTEN rather than because it is wrong: while it is live, the
 * admin's own console is another business's console. So the expiry is not a
 * detail of the row, it is the thing the interface is built around — every
 * screen shows how long is left, and the number has to be honest about being
 * nearly up rather than reading "1h" until the moment it stops working.
 *
 * Pure, and separated from the pages for the usual reason: the rounding at the
 * boundaries is exactly the sort of thing that is wrong for weeks in a template.
 */

// The bounds the SQL enforces — see `admin_impersonate` in
// 13-impersonation.sql, which clamps rather than refuses. Mirrored here so the
// form cannot offer a number the database would silently change under it.
var MIN_MINUTES = 5;
var MAX_MINUTES = 480;
var DEFAULT_MINUTES = 30;

// The four the button offers. A support call is either "look at something now"
// or "sit with them through a shift"; the middle of that range is where nobody
// actually is.
var CHOICES = [15, 30, 60, 240];

/* Empty and null are checked BEFORE the arithmetic, not after.
 *
 * `Number('')` and `Number(null)` are both 0 — finite, so a test for NaN lets
 * them through, and the clamp then turns "nothing typed" into the five-minute
 * floor. Five minutes is a plausible-looking session that ends while somebody is
 * still signing in to the console, which is a worse answer than the default and
 * an easy one to mistake for deliberate.
 */
function clampMinutes(value) {
  if (value === '' || value == null) return DEFAULT_MINUTES;
  var n = Math.round(Number(value));
  if (!isFinite(n)) return DEFAULT_MINUTES;
  return Math.min(MAX_MINUTES, Math.max(MIN_MINUTES, n));
}

/* How long is left, as something to render.
 *
 * `expired` is decided by the same comparison the database makes — the row is
 * live while `expires_at > now()` and not one second longer — so the banner and
 * the policies cannot disagree about whether a session exists.
 *
 * The label counts DOWN through minutes rather than switching to hours early: at
 * 90 minutes "1h 30m" is fine, at 4 minutes "4m" is the whole point, and there is
 * no reading of "in an hour" that helps somebody about to lose their session
 * mid-edit.
 */
function remaining(expiresAt, now) {
  if (!expiresAt) return { expired: true, ms: 0, minutes: 0, label: '', urgent: false };
  var ms = new Date(expiresAt).getTime() - (now || Date.now());
  if (!(ms > 0)) return { expired: true, ms: 0, minutes: 0, label: 'expired', urgent: true };

  var minutes = Math.floor(ms / 60000);
  var label;
  if (minutes < 1) label = 'under a minute';
  else if (minutes < 60) label = minutes + 'm';
  else {
    var h = Math.floor(minutes / 60);
    var m = minutes % 60;
    label = m ? h + 'h ' + m + 'm' : h + 'h';
  }

  // Under five minutes is worth colouring: it is less than the shortest session
  // anybody can start, so it is always nearly over rather than merely short.
  return { expired: false, ms: ms, minutes: minutes, label: label, urgent: minutes < 5 };
}

export const supportSession = {
  MIN_MINUTES: MIN_MINUTES,
  MAX_MINUTES: MAX_MINUTES,
  DEFAULT_MINUTES: DEFAULT_MINUTES,
  CHOICES: CHOICES,
  clampMinutes: clampMinutes,
  remaining: remaining
};
